import { useMutation } from '@tanstack/react-query';
import { endpoints, getIDFromUrl } from '@rossum/api-client';
import { api } from '../../lib/apiClient';
import { EditDocumentConfig } from './editState';

type UseCancelEditModeParams = {
  /**
   * Resolves the annotation the user is being redirected to. Called lazily from
   * inside the mutation, because the mutation runs while the component unmounts
   * — at which point the URL has already changed — so the destination cannot be
   * captured up front.
   */
  getTargetAnnotationId: () => number;
};

export const useCancelEditMode = ({
  getTargetAnnotationId,
}: UseCancelEditModeParams) => {
  return useMutation({
    mutationFn: (config: EditDocumentConfig) => {
      const targetAnnotationId = getTargetAnnotationId();

      // TODO confirmPayload.stopReviewing doesn't contain deleted annotations, otherwise it's the same
      // It would be good to unify this logic somehow
      const annotationsToCancel = config.initialParts.flatMap(p =>
        p.annotationType === 'accessible' &&
        p.annotation.started &&
        // There is a separate cancelParent flag for stopping the original annotation
        p.annotation.url !== config.annotationUrl &&
        // If we are redirecting back to a child annotation, we shouldn't cancel it here,
        // otherwise there is a race condition, and the user might be kicked out.
        getIDFromUrl(p.annotation.url) !== targetAnnotationId
          ? [p.annotation.url]
          : []
      );

      return api.request(
        endpoints.annotations.cancelEditPages(
          getIDFromUrl(config.annotationUrl),
          {
            // If there are no child annotations, we still want to cancel the parent
            annotations: annotationsToCancel,
            // We always want to cancel the parent annotation (config.annotationUrl)
            cancelParent: annotationsToCancel.length !== 0,
          }
        )
      );
    },
  });
};
